/**
 * Scorecards — the traditional grid, per round.
 *
 * Gross above, net below, pops as a dot on the hole number, and the hole
 * outcome shading that the format produced.
 */

import { Fragment, useEffect, useRef, useState } from 'react'
import { clockTime } from '../engine/clock'
import { matchNameIn } from '../engine/naming'
import { matchCounts } from '../engine/types'
import type { Match, Tournament, UnitTab } from '../engine/types'
import { computeAll, matchesInPlayOrder, outstandingCards, playerName, roundDay, todayLocal } from '../select'
import { mergedConfirmations, mergedScores, useStore } from '../store'
import { Bar, Empty } from '../ui'
import { Nassau } from './Nassau'
import { Preview } from './Preview'
import { Rules } from './Rules'
import { Field } from './Field'
import { SideMoney } from './SideBoard'

export type CardView = 'cards' | 'preview' | 'rules' | 'field'

/**
 * The card, sorted the way it was played: foursome first, then sides.
 *
 * `tabs` come back in whatever order the format produced them, which for a
 * 2v2 interleaves the two sides of different groups. Regrouping here keeps a
 * match together on the page and, for the formats where a foursome contains two
 * separate contests, keeps those apart.
 */
function groupsOf(
  doc: Tournament,
  entry: { tabs: UnitTab[] },
): { group: Tournament['groups'][number] | undefined; tabs: UnitTab[] }[] {
  const byGroup = new Map<string, UnitTab[]>()
  for (const t of entry.tabs) {
    const list = byGroup.get(t.groupId) ?? []
    list.push(t)
    byGroup.set(t.groupId, list)
  }
  return [...byGroup.entries()]
    .map(([id, tabs]) => ({ group: doc.groups.find((g) => g.id === id), tabs }))
    .sort((a, b) => (a.group?.index ?? 99) - (b.group?.index ?? 99))
}

/**
 * Who is playing whom, on the row that heads a foursome's card.
 *
 * A singles round is TWO matches inside one foursome, and this line joined
 * every unit with " v " — so Sunday's card announced "Phil v Springer v Thomas
 * v Scott P.", a four-man match nobody is playing. The contests have to be kept
 * apart, which is the same thing `groupsOf` above exists to do for the rows.
 *
 * Read off the pairings rather than off the tabs, because the pairing IS the
 * contest. A format with no pairings — quota — has no "v" in it at all, and
 * saying so plainly beats inventing one.
 */
export function foursomeLine(
  doc: Pick<Tournament, 'pairings' | 'players'>,
  matchId: string,
  tabs: UnitTab[],
): string {
  const named = (ids: readonly string[]) => ids.map((p) => playerName(doc as Tournament, p)).join(' & ')
  const contests = doc.pairings
    .filter((p) => p.matchId === matchId && p.sides.some((sd) => tabs.some((t) => t.unitId === sd.id)))
    .map((p) => p.sides.map((sd) => named(sd.playerIds)).join(' v '))
  if (contests.length > 0) return contests.join(' · ')
  return tabs.map((t) => named(t.playerIds)).join(' · ')
}

/**
 * How a match reads in the picker.
 *
 * The derived name plus one thing the name has no business carrying: whether
 * the round pays anything. A picker that lists a round played for fun beside
 * eight that pay owes the reader the difference, and it is the only screen
 * where they sit in one list.
 */
export function pickerLabel(
  doc: Pick<Tournament, 'rounds' | 'courses'>,
  match: Match,
): string {
  const name = matchNameIn(doc, match)
  return matchCounts(match) ? name : `${name} — for fun`
}

/**
 * Copy a link to this match.
 *
 * The link is worth sending because of what it says before anyone taps it: the
 * match and who is playing whom, which is exactly the thing somebody would
 * otherwise type out by hand into the thread.
 */
function ShareMatch({
  doc,
  entry,
}: {
  doc: Tournament
  entry: { match: Match; tabs: UnitTab[] }
}) {
  const [copied, setCopied] = useState(false)
  // The year rides along because match ids restart at s0001 every tournament,
  // so the id alone stops identifying anything the moment there are two.
  const url = `${location.origin}/m/${entry.match.id}?t=${encodeURIComponent(doc.id)}`

  const line = [
    matchNameIn(doc, entry.match),
    ...doc.pairings
      .filter((p) => p.matchId === entry.match.id)
      .map((p) =>
        p.sides.map((s) => s.playerIds.map((id) => playerName(doc, id)).join(' & ')).join(' v '),
      ),
  ].join(' · ')

  const share = async () => {
    // The system sheet is the right thing on a phone — it puts the link into
    // the thread it is going to end up in anyway. Clipboard is the fallback for
    // a desktop, and for the in-app browsers that do not implement it.
    const data = { title: line, text: line, url }
    try {
      if (navigator.share) {
        await navigator.share(data)
        return
      }
    } catch {
      // Canceled, or refused. Fall through and copy instead.
    }
    try {
      await navigator.clipboard.writeText(`${line}\n${url}`)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={share}
        className="bc-tap w-full border-2 border-ink bc-label py-3"
      >
        {copied ? 'Link copied' : 'Share this match'}
      </button>
      <p className="text-[13px] text-mute leading-snug mt-2">
        Opens straight to this card, and unfurls as &ldquo;{line}&rdquo;.
      </p>
    </>
  )
}

export function Cards({
  doc,
  matchId,
  onMatch,
  focusCell,
  section,
  onSection,
  onSign,
  onFix,
}: {
  doc: Tournament
  /**
   * Which match is open, owned by `App` for the same reason `section` is.
   *
   * Both halves of this screen's navigation used to be local state seeded from a
   * remembered cursor, and both could be outranked by the wrong thing. The match
   * was worse than it looked: `App` holds the id of the last leaderboard row
   * tapped and never clears it, so on every remount that id beat the one you had
   * since chosen in the picker below — pick match 5, glance at the Score tab, come
   * back to match 2.
   *
   * One owner, one value, and the picker reports upward. Null means nobody has
   * chosen, which resolves to the first match of the week.
   */
  matchId: string | null
  onMatch: (id: string) => void
  /** The one square to go to — set when you arrived by tapping a birdie. */
  focusCell?: { playerId: string; holeNo: number } | null
  /**
   * Which of the four sub-screens is showing, owned by `App`.
   *
   * Controlled rather than local, and that is the fix for a real bug. It was
   * `useState` seeded from a remembered cursor, so tapping a birdie in the feed
   * switched to the Cards TAB and then opened whatever sub-tab you happened to
   * have been on last — landing you on "The week" instead of on the card the
   * notification was about. An explicit arrival has to beat memory, and the only
   * way to guarantee that is for whoever knows the intent to own the state.
   *
   * `App` does not unmount, so lifting it also keeps the place across a trip to
   * another tab for free, which is what the cursor was for.
   */
  section: CardView
  onSection: (v: CardView) => void
  /**
   * Jump into the scorer at one exact cell — Scott's ask from the property:
   * a wrong number or a hole nobody entered is FOUND on this grid, so the
   * grid is where the fix has to start. Tap the cell, land on the hole.
   */
  onFix?: (roundId: string, groupId: string, holeNo: number) => void
  /** Jump to the Score tab on a round — the week view offers it for unsigned cards. */
  onSign?: (roundId: string) => void
}) {
  const scores = useStore(mergedScores)
  const confirmations = useStore(mergedConfirmations)
  // Whose side money to show at the foot of the card — yours, not the card's.
  const me = useStore((st) => st.session?.playerId ?? '')
  const view = computeAll(doc, scores, confirmations)
  const [nassau, setNassau] = useState(false)
  /*
   * Both nines as one card. Declared with the other hooks and above every
   * early return in this component — the sub-screens below return before
   * the card is built, and a hook after them changes the hook count between
   * renders. React answers that with "Rendered fewer hooks than expected",
   * which is a white screen rather than a warning.
   */
  const [both, setBoth] = useState(false)
  /*
   * Men picked out for comparison. Empty means the whole field in its
   * foursomes; any pick means those men alone, side by side, out of their
   * groups — because comparing two rounds is a different job from reading a
   * card, and the foursome is only in the way of it.
   */
  const [compare, setCompare] = useState<string[]>([])
  /*
   * Extra eighteens laid beside the one on show — Scott's ask. A man's
   * Monday against his Tuesday is the comparison that settles arguments,
   * and it needs two cards on one screen rather than a toggle between them.
   * Keyed by date~course, the same key the picker uses.
   */
  const [alsoShow, setAlsoShow] = useState<string[]>([])
  /*
   * Validated against THIS document before it is used. An id held over from the
   * other tournament — both number their matches from s0001 — would otherwise
   * resolve to `Object.values(view.byMatch)[0]` and open a card nobody asked for.
   */
  const openId =
    matchId && doc.matches.some((m) => m.id === matchId)
      ? matchId
      : (matchesInPlayOrder(doc)[0]?.id ?? '')
  const entry = view.byMatch[openId] ?? Object.values(view.byMatch)[0]

  /*
   * Bring the asked-for square into view once it is on screen.
   *
   * The card is a twelve-row table that scrolls sideways inside its own box, so
   * a hole past the 4th is off the right edge on a phone and the player may be
   * two foursomes down. `scrollIntoView` walks every scrollable ancestor, which
   * is exactly what is needed here — the horizontal box AND the page — and is
   * the reason this is a ref rather than arithmetic on offsets.
   *
   * Dismissed by the first thing the reader does: picking another match drops
   * the highlight, because by then it is pointing at a square nobody asked
   * about. That is `cell` being cleared in the select below, not here.
   */
  const cellRef = useRef<HTMLTableCellElement | null>(null)
  const [cell, setCell] = useState(focusCell ?? null)
  useEffect(() => {
    setCell(focusCell ?? null)
  }, [focusCell])
  useEffect(() => {
    if (!cell || section !== 'cards') return
    /*
     * Aimed more than once, because the target moves while it is being aimed at.
     *
     * Arriving here means the tab has just switched and this whole table has
     * only just mounted, so the first attempt is computed against a layout that
     * is still settling — the twelve rows, the sticky first column and the
     * horizontal scrollbox all resolve over the following frames. Scrolling
     * once, on whatever frame happens to be next, landed the card three hundred
     * pixels short with the birdie under the tab bar; moving it a frame later
     * missed in the other direction. Frame counting was the wrong instrument.
     *
     * So it re-aims a few times over the first half second. Once the geometry
     * has settled `scrollIntoView` is a no-op, so the repeats cost nothing and
     * stop being visible the moment they stop being needed.
     *
     * Instant rather than smooth, deliberately: an animation started from a
     * stale position glides confidently to the wrong square, and this is a tap
     * that means "show me the thing", not a tour of the page.
     */
    const go = () =>
      cellRef.current?.scrollIntoView({ block: 'center', inline: 'center', behavior: 'auto' })
    go()
    const timers = [80, 220, 500].map((ms) => setTimeout(go, ms))
    return () => timers.forEach(clearTimeout)
  }, [cell, section, openId])

  if (nassau) return <Nassau doc={doc} onBack={() => setNassau(false)} />

  const modes = (
    /* Four across, so "Scorecards" and "The rules" shorten to fit 98px. */
    <div className="grid grid-cols-4 border-b border-rule">
      {(['cards', 'preview', 'field', 'rules'] as const).map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onSection(m)}
          className={`bc-label bc-tab py-2.5 bc-tap text-center ${
            section === m ? 'bg-ink text-paper' : 'bg-paper text-mute'
          }`}
        >
          {m === 'cards'
            ? 'Cards'
            : m === 'preview'
              ? 'The week'
              : m === 'field'
                ? 'The field'
                : 'Rules'}
        </button>
      ))}
    </div>
  )

  if (section === 'preview') {
    return (
      <>
        <Preview doc={doc} header={modes} onSign={onSign} />
      </>
    )
  }

  if (section === 'rules') {
    return (
      <>
        <Rules doc={doc} header={modes} />
      </>
    )
  }

  if (section === 'field') {
    return (
      <>
        <Field doc={doc} header={modes} />
      </>
    )
  }

  if (!entry) {
    return (
      <>
        <Bar title="Cards" />
        {modes}
        <Empty>No rounds set up yet.</Empty>
      </>
    )
  }

  /*
   * Both nines of one course as one card — Scott's ask.
   *
   * The week is scored in nines because that is how the money is played,
   * but a man reading a card wants the eighteen he walked. The partner is
   * the other nine of the SAME course on the SAME day; anything else is a
   * different round of golf and joining them would invent a card nobody
   * played. The foursomes re-pair at the turn, so the combined view drops
   * the per-foursome grouping and lists the field — honest, since a
   * combined card is a scorecard rather than a match sheet.
   */
  const partner = (() => {
    const r = doc.rounds.find((x) => x.id === entry?.match.roundId)
    if (!r || (r.nine !== 'front' && r.nine !== 'back')) return null
    const other = doc.rounds.find(
      (x) =>
        x.id !== r.id &&
        x.courseId === r.courseId &&
        x.date === r.date &&
        (x.nine === 'front' || x.nine === 'back') &&
        x.nine !== r.nine,
    )
    if (!other) return null
    const m = doc.matches.find((x) => x.roundId === other.id)
    const e = m ? view.byMatch[m.id] : undefined
    return e && r.nine === 'front' ? { entry: e, after: true } : e ? { entry: e, after: false } : null
  })()
  const combined = both && partner != null

  const holes = combined
    ? partner!.after
      ? [...(entry.tabs[0]?.perHole ?? []), ...(partner!.entry.tabs[0]?.perHole ?? [])]
      : [...(partner!.entry.tabs[0]?.perHole ?? []), ...(entry.tabs[0]?.perHole ?? [])]
    : (entry.tabs[0]?.perHole ?? [])

  /**
   * The combined card, kept in the morning's foursomes — Scott's ruling.
   *
   * The draw re-pairs at the turn, so an eighteen has two sets of groups
   * and only one can order the page. The FRONT nine wins: it is who a man
   * walked to the first tee with, and it is the order the day is
   * remembered in. His back nine follows him into that row wherever the
   * afternoon draw put him.
   */
  const wholeRound = (() => {
    if (!combined) return []
    const first = partner!.after ? entry : partner!.entry
    const second = partner!.after ? partner!.entry : entry
    const cellsFor = (e: typeof entry, pid: string) => {
      const tab = e.tabs.find((t) => t.playerIds.includes(pid))
      return (tab?.perHole ?? []).map((h) => ({
        holeNo: h.holeNo,
        par: h.par,
        gross: h.gross[pid],
        pops: h.pops[pid] ?? 0,
      }))
    }
    const frontGroups = doc.groups
      .filter((g) => g.roundId === first.match.roundId)
      .sort((a, b) => a.index - b.index)
    const seen = new Set<string>()
    const sections = frontGroups.map((g) => ({
      key: g.id,
      teeTime: g.teeTime,
      men: g.playerIds.map((pid) => {
        seen.add(pid)
        return { pid, cells: [...cellsFor(first, pid), ...cellsFor(second, pid)] }
      }),
    }))
    // Anybody the front nine never saw still belongs on the card.
    const strays = [...new Set(second.tabs.flatMap((t) => t.playerIds))]
      .filter((pid) => !seen.has(pid))
      .map((pid) => ({ pid, cells: [...cellsFor(first, pid), ...cellsFor(second, pid)] }))
    const all =
      strays.length > 0 ? [...sections, { key: 'rest', teeTime: '', men: strays }] : sections
    /*
     * Picked men come out of their foursomes and stand together, in the
     * order they were picked — comparing two rounds is not reading a card,
     * and the group headings are only in the way of it.
     */
    if (compare.length > 0) {
      const byId = new Map(all.flatMap((sec) => sec.men.map((m) => [m.pid, m] as const)))
      const men = compare.map((pid) => byId.get(pid)).filter((m): m is NonNullable<typeof m> => !!m)
      return men.length > 0 ? [{ key: 'compare', teeTime: '', men }] : all
    }
    return all
  })()

  /*
   * Every eighteen the week contains, named by day and course. Two nines of
   * one course on one day make one; a round already played over eighteen is
   * one on its own. This is the list a man means when he says "show me
   * Monday at Bandon" rather than hunting for the front-nine match.
   */
  const eighteens = (() => {
    const seen = new Set<string>()
    const out: { key: string; label: string; matchId: string }[] = []
    for (const m of matchesInPlayOrder(doc)) {
      const r = doc.rounds.find((x) => x.id === m.roundId)
      if (!r) continue
      const key = `${r.date}~${r.courseId}`
      if (seen.has(key)) continue
      const course = doc.courses.find((c) => c.id === r.courseId)
      const nines = doc.rounds.filter(
        (x) => x.date === r.date && x.courseId === r.courseId,
      )
      const isEighteen = r.nine === 'full18' || nines.length >= 2
      if (!isEighteen) continue
      seen.add(key)
      out.push({
        key,
        label: `${roundDay(r.date)} · ${course?.name ?? r.label}`,
        matchId: m.id,
      })
    }
    return out
  })()

  /** The two halves, for the Out/In breaks a scorecard is read by. */
  const outPar = holes.slice(0, 9).reduce((n, h) => n + h.par, 0)
  const inPar = holes.slice(9).reduce((n, h) => n + h.par, 0)

  /*
   * The extra eighteens, each carrying its own holes and par — Old Mac's
   * card is not Bandon's, and laying one under the other's par row would
   * print a lie. Only the picked men appear on them: comparing is the whole
   * reason a second card is on screen.
   */
  const extraBoards = (() => {
    if (!combined || alsoShow.length === 0 || compare.length === 0) return []
    return alsoShow
      .map((key) => {
        const e = eighteens.find((x) => x.key === key)
        if (!e) return null
        const first = view.byMatch[e.matchId]
        if (!first) return null
        const r = doc.rounds.find((x) => x.id === first.match.roundId)
        const other = doc.rounds.find(
          (x) => x.id !== r?.id && x.date === r?.date && x.courseId === r?.courseId,
        )
        const om = other ? doc.matches.find((m) => m.roundId === other.id) : undefined
        const second = om ? view.byMatch[om.id] : undefined
        const cells = (en: typeof first | undefined, pid: string) => {
          const tab = en?.tabs.find((t) => t.playerIds.includes(pid))
          return (tab?.perHole ?? []).map((h) => ({
            holeNo: h.holeNo,
            par: h.par,
            gross: h.gross[pid],
            pops: h.pops[pid] ?? 0,
          }))
        }
        const men = compare
          .map((pid) => ({ pid, cells: [...cells(first, pid), ...cells(second, pid)] }))
          .filter((m) => m.cells.length > 0)
        if (men.length === 0) return null
        const bh = men[0]!.cells
        return {
          key,
          label: e.label,
          holes: bh,
          outPar: bh.slice(0, 9).reduce((n, c) => n + c.par, 0),
          inPar: bh.slice(9).reduce((n, c) => n + c.par, 0),
          men,
        }
      })
      .filter((b): b is NonNullable<typeof b> => b !== null)
  })()
  /*
   * The Par row's total is the par of the holes being played.
   *
   * This summed the first nine and printed the result in the Tot column, which
   * is right by accident on the seven nine-hole rounds and says "34" on Bandon
   * Trails and the Championship — under a row of players totalling 71. The card
   * claimed the course was a par 34.
   */
  const totalPar = holes.reduce((s, h) => s + h.par, 0)

  /*
   * Which of this round's foursomes have signed, and which are outstanding.
   *
   * Two sets rather than one because there is a real third state. A group that
   * has not signed AND has not played is not a problem to report — on an
   * untouched future round every row would read UNSIGNED, which is true and
   * useless. `outstandingCards` already draws exactly that line, so the second
   * set is its answer filtered to this round rather than a rule restated here.
   */
  const signedGroups = new Set(
    confirmations.filter((c) => c.roundId === entry.round.id).map((c) => c.groupId),
  )
  const unsignedGroups = new Set(
    outstandingCards(doc, scores, confirmations, todayLocal())
      .filter((c) => c.roundId === entry.round.id)
      .map((c) => c.groupId),
  )

  return (
    <>
      {/*
        The COURSE is the title — Scott's call. "Cards" was a label naming the
        tab you were already on, while the one thing a man flipping between
        cards needs anchored is which course this card belongs to. The match
        line below still says the nine and the game.
      */}
      <Bar
        kicker="Cards"
        title={doc.courses.find((c) => c.id === entry.round.courseId)?.name ?? entry.round.label}
        sub={matchNameIn(doc, entry.match)}
      />
      {modes}

      {doc.matches.length > 1 && (
        <div className="px-4 py-3 border-b border-rule flex items-center gap-2">
          <select
            value={entry.match.id}
            onChange={(e) => {
              onMatch(e.target.value)
              // Whoever it was pointing at is not on this card.
              setCell(null)
            }}
            className="bc-label border border-ink px-2 py-2 bc-tap bg-paper flex-1 min-w-0"
          >
            {matchesInPlayOrder(doc).map((m) => (
              <option key={m.id} value={m.id}>
                {pickerLabel(doc, m)}
              </option>
            ))}
          </select>
          {/* Only where there IS an eighteen — a lone nine has nothing to
              join, and offering the toggle would promise one. */}
          {partner && (
            <button
              type="button"
              onClick={() => setBoth((v) => !v)}
              aria-pressed={both}
              className={`bc-tap bc-label px-3 py-2 border border-ink shrink-0 whitespace-nowrap ${
                both ? 'bg-ink text-paper' : 'bg-paper'
              }`}
            >
              All 18
            </button>
          )}
        </div>
      )}

      {/*
        The eighteen's own controls: which day's card, and which men to lay
        side by side. Only here — a nine is read as a card, and an eighteen
        is what men compare.
      */}
      {combined && (
        <div className="px-4 py-2.5 border-b border-rule grid gap-2">
          {eighteens.length > 1 && (
            <div>
              <span className="bc-label text-mute block mb-1.5">
                Which eighteen{alsoShow.length > 0 ? ` — ${alsoShow.length + 1} shown` : ''}
              </span>
              {/*
                Tap to show; tap again to put away. The first tap on a card
                that is not on show makes it THE card, so one tap still means
                "take me there"; tapping a second card stacks it underneath
                rather than replacing — which is the whole point of comparing
                a Monday against a Tuesday.
              */}
              <div className="flex flex-wrap gap-1.5">
                {eighteens.map((e) => {
                  const isMain = e.matchId === entry.match.id
                  const on = isMain || alsoShow.includes(e.key)
                  return (
                    <button
                      key={e.key}
                      type="button"
                      aria-pressed={on}
                      onClick={() => {
                        if (isMain) return
                        setAlsoShow((cur) =>
                          cur.includes(e.key)
                            ? cur.filter((x) => x !== e.key)
                            : cur.length === 0 && alsoShow.length === 0
                              ? cur
                              : [...cur, e.key],
                        )
                        if (!alsoShow.includes(e.key) && alsoShow.length === 0) {
                          onMatch(e.matchId)
                          setCell(null)
                        }
                      }}
                      className={`bc-tap bc-label text-[11px] px-2 py-1 border ${
                        isMain
                          ? 'bg-ink text-paper border-ink'
                          : on
                            ? 'bg-wash text-ink border-ink'
                            : 'border-rule bg-paper text-mute'
                      }`}
                    >
                      {e.label}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
          <div>
            <div className="flex items-baseline justify-between gap-3 mb-1.5">
              <span className="bc-label text-mute">
                {compare.length === 0
                  ? 'Compare men'
                  : `Comparing ${compare.length}`}
              </span>
              {compare.length > 0 && (
                <button
                  type="button"
                  onClick={() => setCompare([])}
                  className="bc-tap bc-label text-[11px] text-signal"
                >
                  Show the field
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {doc.players
                .filter((p) => p.active)
                .map((p) => {
                  const on = compare.includes(p.id)
                  return (
                    <button
                      key={p.id}
                      type="button"
                      aria-pressed={on}
                      onClick={() =>
                        setCompare((cur) =>
                          cur.includes(p.id)
                            ? cur.filter((x) => x !== p.id)
                            : [...cur, p.id],
                        )
                      }
                      className={`bc-tap bc-label text-[11px] px-2 py-1 border ${
                        on ? 'bg-ink text-paper border-ink' : 'border-rule bg-paper text-mute'
                      }`}
                    >
                      {p.displayName}
                    </button>
                  )
                })}
            </div>
          </div>
        </div>
      )}

      {/* The card is wider than a phone; it scrolls inside its own container so
          the page body never moves sideways. */}
      <div className="overflow-x-auto">
        <table className="text-xs border-collapse min-w-max">
          <thead>
            <tr className="bg-ink text-paper">
              <th className="bc-label text-left px-3 py-2 sticky left-0 bg-ink z-10">
                Hole
              </th>
              {holes.map((h, i) => (
                <Fragment key={`${h.holeNo}-${i}`}>
                  <th className="bc-num px-2 py-2 w-9">{h.holeNo}</th>
                  {/* The turn, where a scorecard has always totalled. */}
                  {combined && i === 8 && <th className="bc-label px-2 py-2">Out</th>}
                </Fragment>
              ))}
              {combined && <th className="bc-label px-2 py-2">In</th>}
              <th className="bc-label px-3 py-2">Tot</th>
              {/* Where he finished against the card — the number a man says
                  when asked how he played. */}
              <th className="bc-label px-3 py-2">To par</th>
            </tr>
            <tr className="bg-wash">
              <th className="bc-label text-left px-3 py-1 sticky left-0 bg-wash z-10">
                Par
              </th>
              {holes.map((h, i) => (
                <Fragment key={`${h.holeNo}-${i}`}>
                  <th className="bc-num px-2 py-1 text-mute font-semibold">{h.par}</th>
                  {combined && i === 8 && (
                    <th className="bc-num px-2 py-1 text-mute font-semibold">{outPar}</th>
                  )}
                </Fragment>
              ))}
              {combined && (
                <th className="bc-num px-2 py-1 text-mute font-semibold">{inPar}</th>
              )}
              <th className="bc-num px-3 py-1 text-mute">{totalPar}</th>
              <th className="bc-num px-3 py-1 text-mute">&mdash;</th>
            </tr>
          </thead>
          <tbody>
            {combined ? (
              /*
                A scorecard, read the way one is read: the morning foursome
                heads the block, the turn totals at Out, the second nine at
                In, and the day at Tot — each with the par it was played
                against and how far from it the man finished.
              */
              wholeRound.map((section) => (
                <Fragment key={section.key}>
                  <tr className="bg-ink text-paper">
                    <td className="bc-label px-3 py-1.5 sticky left-0 bg-ink z-10 whitespace-nowrap">
                      {section.key === 'compare'
                        ? 'Side by side'
                        : section.teeTime
                          ? clockTime(section.teeTime)
                          : 'Not in the morning draw'}
                    </td>
                    <td colSpan={holes.length + 3} className="bc-label px-3 py-1.5">
                      {section.men.map((m) => playerName(doc, m.pid)).join(' · ')}
                    </td>
                  </tr>
                  {section.men.map((row, ri) => {
                    const half = (from: number, to: number) => {
                      const cells = row.cells.slice(from, to).filter((c) => c.gross != null)
                      const strokes = cells.reduce((n, c) => n + (c.gross ?? 0), 0)
                      const par = cells.reduce((n, c) => n + c.par, 0)
                      return { strokes, diff: strokes - par, any: cells.length > 0 }
                    }
                    const out = half(0, 9)
                    const inn = half(9, 18)
                    const all = half(0, 18)
                    const toPar = (d: number) => (d === 0 ? 'E' : d > 0 ? `+${d}` : `${d}`)
                    const tone = (d: number) =>
                      d < 0 ? 'text-signal' : d === 0 ? '' : 'text-mute'
                    const half_cell = (h: typeof out) => (
                      <td className="bc-num px-2 py-2 text-center bg-wash whitespace-nowrap">
                        {h.any ? (
                          <>
                            {h.strokes}
                            <span className={`text-[11px] ml-1 ${tone(h.diff)}`}>
                              {toPar(h.diff)}
                            </span>
                          </>
                        ) : (
                          <span className="text-dim">·</span>
                        )}
                      </td>
                    )
                    return (
                      <tr
                        key={row.pid}
                        className={
                          ri === section.men.length - 1
                            ? 'border-b-2 border-ink'
                            : 'border-b border-rule'
                        }
                      >
                        <td
                          className={`bc-name px-3 py-2 sticky left-0 z-10 whitespace-nowrap ${
                            row.pid === me ? 'bg-ink text-paper' : 'bg-paper'
                          }`}
                        >
                          {playerName(doc, row.pid)}
                        </td>
                        {row.cells.map((c, ci) => {
                          const diff = c.gross == null ? null : c.gross - c.par
                          return (
                            <Fragment key={`${c.holeNo}-${ci}`}>
                              <td
                                className={`bc-num px-2 py-2 text-center relative ${
                                  diff == null
                                    ? 'text-dim'
                                    : diff < 0
                                      ? `bg-signal text-paper${c.pops > 0 ? ' bc-popfill' : ''}`
                                      : diff === 0
                                        ? ''
                                        : 'text-mute'
                                }`}
                              >
                                {c.gross ?? '·'}
                                {c.pops > 0 && (
                                  <span
                                    aria-hidden="true"
                                    className="pointer-events-none absolute inset-0 border-[3px] border-pop"
                                  />
                                )}
                              </td>
                              {ci === 8 && half_cell(out)}
                            </Fragment>
                          )
                        })}
                        {half_cell(inn)}
                        <td className="bc-num px-3 py-2 text-center">
                          {all.any ? all.strokes : '·'}
                        </td>
                        <td className={`bc-num px-3 py-2 text-center ${tone(all.diff)}`}>
                          {all.any ? toPar(all.diff) : '·'}
                        </td>
                      </tr>
                    )
                  })}
                </Fragment>
              ))
            ) : null}
            {combined &&
              extraBoards.map((b) => (
                <Fragment key={b.key}>
                  {/* Its own par row: another course, another card. */}
                  <tr className="bg-ink text-paper">
                    <td className="bc-label px-3 py-1.5 sticky left-0 bg-ink z-10 whitespace-nowrap">
                      {b.label}
                    </td>
                    <td colSpan={holes.length + 3} className="bc-label px-3 py-1.5">
                      {b.men.map((m) => playerName(doc, m.pid)).join(' · ')}
                    </td>
                  </tr>
                  <tr className="bg-wash">
                    <td className="bc-label px-3 py-1 sticky left-0 bg-wash z-10 text-mute">
                      Par
                    </td>
                    {b.holes.map((c, i) => (
                      <Fragment key={`${b.key}-p-${i}`}>
                        <td className="bc-num px-2 py-1 text-center text-mute">{c.par}</td>
                        {i === 8 && (
                          <td className="bc-num px-2 py-1 text-center text-mute">{b.outPar}</td>
                        )}
                      </Fragment>
                    ))}
                    <td className="bc-num px-2 py-1 text-center text-mute">{b.inPar}</td>
                    <td className="bc-num px-3 py-1 text-center text-mute">
                      {b.outPar + b.inPar}
                    </td>
                    <td className="bc-num px-3 py-1 text-center text-mute">&mdash;</td>
                  </tr>
                  {b.men.map((row, ri) => {
                    const half = (from: number, to: number) => {
                      const cells = row.cells.slice(from, to).filter((c) => c.gross != null)
                      const strokes = cells.reduce((n, c) => n + (c.gross ?? 0), 0)
                      const par = cells.reduce((n, c) => n + c.par, 0)
                      return { strokes, diff: strokes - par, any: cells.length > 0 }
                    }
                    const out = half(0, 9)
                    const inn = half(9, 18)
                    const all = half(0, 18)
                    const toPar = (d: number) => (d === 0 ? 'E' : d > 0 ? `+${d}` : `${d}`)
                    const tone = (d: number) => (d < 0 ? 'text-signal' : d === 0 ? '' : 'text-mute')
                    const halfCell = (h: typeof out, k: string) => (
                      <td
                        key={k}
                        className="bc-num px-2 py-2 text-center bg-wash whitespace-nowrap"
                      >
                        {h.any ? (
                          <>
                            {h.strokes}
                            <span className={`text-[11px] ml-1 ${tone(h.diff)}`}>
                              {toPar(h.diff)}
                            </span>
                          </>
                        ) : (
                          <span className="text-dim">·</span>
                        )}
                      </td>
                    )
                    return (
                      <tr
                        key={`${b.key}-${row.pid}`}
                        className={
                          ri === b.men.length - 1
                            ? 'border-b-2 border-ink'
                            : 'border-b border-rule'
                        }
                      >
                        <td
                          className={`bc-name px-3 py-2 sticky left-0 z-10 whitespace-nowrap ${
                            row.pid === me ? 'bg-ink text-paper' : 'bg-paper'
                          }`}
                        >
                          {playerName(doc, row.pid)}
                        </td>
                        {row.cells.map((c, ci) => {
                          const diff = c.gross == null ? null : c.gross - c.par
                          return (
                            <Fragment key={`${b.key}-${row.pid}-${ci}`}>
                              <td
                                className={`bc-num px-2 py-2 text-center relative ${
                                  diff == null
                                    ? 'text-dim'
                                    : diff < 0
                                      ? `bg-signal text-paper${c.pops > 0 ? ' bc-popfill' : ''}`
                                      : diff === 0
                                        ? ''
                                        : 'text-mute'
                                }`}
                              >
                                {c.gross ?? '·'}
                                {c.pops > 0 && (
                                  <span
                                    aria-hidden="true"
                                    className="pointer-events-none absolute inset-0 border-[3px] border-pop"
                                  />
                                )}
                              </td>
                              {ci === 8 && halfCell(out, `${b.key}-${row.pid}-out`)}
                            </Fragment>
                          )
                        })}
                        {halfCell(inn, `${b.key}-${row.pid}-in`)}
                        <td className="bc-num px-3 py-2 text-center">
                          {all.any ? all.strokes : '·'}
                        </td>
                        <td className={`bc-num px-3 py-2 text-center ${tone(all.diff)}`}>
                          {all.any ? toPar(all.diff) : '·'}
                        </td>
                      </tr>
                    )
                  })}
                </Fragment>
              ))}
            {!combined && (
            <>
            {/*
              Grouped by foursome, then by side.
              Twelve players in one unbroken block is a wall of digits — you
              cannot see who was playing whom, which is the only reason to read
              a card next to a result. The tee time heads each group and a heavy
              rule closes it, so the page reads as three cards rather than one.
            */}
            {groupsOf(doc, entry).map(({ group, tabs }, gi) => (
              <Fragment key={group?.id ?? `g${gi}`}>
                <tr className="bg-ink text-paper">
                  <td
                    className="bc-label px-3 py-1.5 sticky left-0 bg-ink z-10 whitespace-nowrap"
                  >
                    {group ? clockTime(group.teeTime) : 'Unassigned'}
                  </td>
                  <td colSpan={holes.length + 1} className="bc-label px-3 py-1.5">
                    <span className="flex items-center gap-3">
                      <span className="min-w-0">
                        {foursomeLine(doc, entry.match.id, tabs)}
                      </span>
                      {/*
                        Whether this foursome signed, on the row that already
                        names them.

                        A card carries no mark for this anywhere else, and the
                        omission is not cosmetic: `computeAll` will not settle a
                        match until EVERY group in its round has signed, so one
                        foursome that walks off unsigned holds up three other
                        men's money while the board keeps cheerfully showing
                        their scores. Reading a card, "did they sign" was
                        unanswerable.

                        Only stated for a group that has played — see the note
                        on the two sets above.
                      */}
                      {group && signedGroups.has(group.id) ? (
                        <span className="bc-label bc-chip ml-auto shrink-0 bg-paper text-ink px-1.5">
                          Signed
                        </span>
                      ) : group && unsignedGroups.has(group.id) ? (
                        <span className="bc-label bc-chip ml-auto shrink-0 bg-signal text-paper px-1.5">
                          Unsigned
                        </span>
                      ) : null}
                    </span>
                  </td>
                </tr>

                {tabs.map((tab, ti) => (
                  <Fragment key={tab.unitId}>
                    {/* Only worth naming a side when a side is a pair. */}
                    {tab.playerIds.length > 1 && (
                      <tr className="bg-wash">
                        <td
                          className="bc-label text-mute px-3 py-1 sticky left-0 bg-wash z-10 whitespace-nowrap"
                          colSpan={holes.length + 2}
                        >
                          {tab.playerIds.map((p) => playerName(doc, p)).join(' & ')} ·{' '}
                          {tab.chip.primary}
                        </td>
                      </tr>
                    )}
                    {tab.playerIds.map((pid) => {
                      const total = tab.perHole.reduce((s, h) => s + (h.gross[pid] ?? 0), 0)
                      const isHim = cell?.playerId === pid
                      return (
                        <tr
                          key={`${tab.unitId}-${pid}`}
                          className={
                            ti === tabs.length - 1 ? 'border-b-2 border-ink' : 'border-b border-rule'
                          }
                        >
                          {/*
                            The name inverts rather than the whole row.
                            Washing the row would fight the red boxes that mark
                            everything under par — including, most of the time,
                            the very square being pointed at. The name is the
                            one cell that is always visible, since it is pinned
                            to the left through every sideways scroll.
                          */}
                          <td
                            className={`bc-name px-3 py-2 sticky left-0 z-10 whitespace-nowrap ${
                              isHim ? 'bg-ink text-paper' : 'bg-paper'
                            }`}
                          >
                            {playerName(doc, pid)}
                          </td>
                          {tab.perHole.map((h) => {
                            const g = h.gross[pid]
                            const pops = h.pops[pid] ?? 0
                            const d = g == null ? null : g - h.par
                            const target = isHim && cell?.holeNo === h.holeNo
                            return (
                              <td
                                key={h.holeNo}
                                ref={target ? cellRef : undefined}
                                /*
                                  The cell is the door to fixing itself: a
                                  wrong 6 or a blank is FOUND here, so a tap
                                  goes straight to that hole in the scorer.
                                */
                                onClick={
                                  group && onFix
                                    ? () => onFix(entry.match.roundId, group.id, h.holeNo)
                                    : undefined
                                }
                                /*
                                 * A ring, not a fill. The square is usually
                                 * already red — it is a birdie, that is why
                                 * somebody tapped through to it — so anything
                                 * that colors the background says nothing.
                                 * Drawn inside the cell's own box so it does
                                 * not nudge the column widths.
                                 *
                                 * The ring is `pop` rather than `signal` so a
                                 * stroke received cannot be mistaken for a
                                 * score, and switches to `onsignal-pop` on an
                                 * under-par cell because there it is sitting on
                                 * the fill instead of on paper.
                                 *
                                 * 3px rather than 2: at 2 it read as the cell's
                                 * own border. The 8px of cell padding leaves 5px
                                 * of clear space around the digit at 3.
                                 */
                                className={`bc-num px-2 py-2 text-center relative ${
                                  d == null
                                    ? 'text-dim'
                                    : d < 0
                                      ? `bg-signal text-paper${pops > 0 ? ' bc-popfill' : ''}`
                                      : d === 0
                                        ? ''
                                        : 'text-mute'
                                } ${
                                  target
                                    ? /*
                                       * `bc-found` pulses it twice and then rests,
                                       * from OUTSIDE the cell — see the note in
                                       * index.css. It replaced an inset outline,
                                       * which was camouflage on a card already
                                       * full of rings and, worse, covered the pop
                                       * ring on the one square somebody had just
                                       * been sent to look at.
                                       *
                                       * `scroll-mb` keeps it clear of the tab bar,
                                       * which is pinned over the foot of the page:
                                       * the last row of the last card cannot be
                                       * centerd however far you scroll, and
                                       * without this it comes to rest underneath
                                       * the navigation.
                                       */
                                      'bc-found z-20 scroll-mb-24 scroll-mt-20'
                                    : ''
                                }`}
                              >
                                {g ?? '·'}
                                {/*
                                  One ring, one color, whatever the score. It
                                  used to switch to a pale on-signal tint on an
                                  under-par cell, which stopped reading as the
                                  pop mark at all — on the fill it looked like
                                  a rendering mistake. Now the FILL moves
                                  instead: `bc-popfill` pulls it in off the
                                  edges (background-clip: content-box), so the
                                  ring always sits on paper and both marks keep
                                  the colors the legend teaches.
                                */}
                                {pops > 0 && (
                                  <span
                                    aria-hidden="true"
                                    className="pointer-events-none absolute inset-0 border-[3px] border-pop"
                                  />
                                )}
                                {pops > 1 && (
                                  <span
                                    className={`absolute bottom-0 right-0.5 text-[9px] font-extrabold leading-none text-pop${
                                      d != null && d < 0 ? ' bg-paper px-0.5' : ''
                                    }`}
                                  >
                                    {pops}
                                  </span>
                                )}
                                <span className="sr-only">
                                  {pops > 0 ? ` (${pops} stroke${pops > 1 ? 's' : ''})` : ''}
                                </span>
                              </td>
                            )
                          })}
                          <td className="bc-num px-3 py-2 text-center">{total || '·'}</td>
                          {(() => {
                            /*
                              Measured over the holes he actually has a score
                              for, so a card in progress reads "level thru 5"
                              rather than eight under for the holes he has not
                              played yet.
                            */
                            const played = tab.perHole.filter((h) => h.gross[pid] != null)
                            if (played.length === 0)
                              return <td className="bc-num px-3 py-2 text-center text-dim">·</td>
                            const d =
                              played.reduce((n, h) => n + (h.gross[pid] ?? 0), 0) -
                              played.reduce((n, h) => n + h.par, 0)
                            return (
                              <td
                                className={`bc-num px-3 py-2 text-center ${
                                  d < 0 ? 'text-signal' : d === 0 ? '' : 'text-mute'
                                }`}
                              >
                                {d === 0 ? 'E' : d > 0 ? `+${d}` : d}
                              </td>
                            )
                          })()}
                        </tr>
                      )
                    })}
                  </Fragment>
                ))}
              </Fragment>
            ))}
            </>
            )}
          </tbody>
        </table>
      </div>

      <p className="px-4 py-4 text-[13px] text-mute leading-snug">
        A filled box is under par. A ringed box is a hole you get a stroke on —
        two strokes prints the number. The two are different colors because they
        are different things: one is what he shot, the other is what he was given.
        A birdie on a stroke hole shows both, the fill sitting inside the ring.
      </p>

      {/*
        What you owe, and to whom, at the foot of the card.
        
        Scott's call, and it is the right shape: nothing is owed person to person
        on TOURNAMENT money — that comes out of the pot — so "what do I owe" is
        entirely side bets, and side bets are already netted per pair. It needed no
        new screen, only to stop being buried one tap inside a side match.
        
        Renders nothing for a man with no side matches, which is most of the field.
      */}
      <SideMoney doc={doc} viewerId={me} />

      <div className="px-4 pb-4">
        <ShareMatch doc={doc} entry={entry} />
      </div>

      <div className="px-4 pb-8">
        <button
          type="button"
          onClick={() => setNassau(true)}
          className="bc-tap w-full bg-signal text-paper bc-label py-4"
        >
          Score a match
        </button>
        <p className="text-[13px] text-mute leading-snug mt-2">
          Two of these cards, head to head. Nassau with presses, walked a hole at a time.
        </p>
      </div>
    </>
  )
}
